import { useCallback, useEffect, useRef, useState } from "react";

import {
  BenchApiError,
  type BenchHealth,
  type Environment,
  type EnvironmentHealth,
  type ScopePatch,
  type SessionView,
  endSession,
  fetchBenchHealth,
  fetchEnvironmentHealth,
  fetchEnvironments,
  fetchSession,
  patchProfile,
  patchScope,
  refreshSession,
  startSession,
} from "../api/bench";

export type BootState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; environments: Environment[]; profiles: string[]; bench: BenchHealth };

export interface Bench {
  boot: BootState;
  session: SessionView | null;
  amsHealth: EnvironmentHealth | null;
  healthLoading: boolean;
  busy: boolean;
  actionError: string | null;
  /** Set when the automatic token renewal failed and the session is running out. */
  renewalFailed: boolean;
  connect: (environment: string, profile: string) => Promise<void>;
  disconnect: () => Promise<void>;
  selectProfile: (profile: string) => Promise<void>;
  applyScope: (patch: ScopePatch) => Promise<void>;
  checkHealth: (environment: string) => Promise<void>;
}

/** Renew this long before expiry, so a slow call in flight cannot outlive its token. */
const RENEW_MARGIN_SECONDS = 120;

/** Never schedule tighter than this, however little is left. */
const MIN_RENEW_DELAY_SECONDS = 5;

/**
 * All bench state in one place.
 *
 * The session lives on the server behind an HttpOnly cookie, so this hook holds only the server's
 * view of it. On mount it asks whether a session already exists, which is what makes a page
 * reload non-destructive.
 */
export function useBench(): Bench {
  const [boot, setBoot] = useState<BootState>({ kind: "loading" });
  const [session, setSession] = useState<SessionView | null>(null);
  const [amsHealth, setAmsHealth] = useState<EnvironmentHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [renewalFailed, setRenewalFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const [list, bench] = await Promise.all([
          fetchEnvironments(controller.signal),
          fetchBenchHealth(controller.signal),
        ]);

        // A 401 here is the normal "not signed in yet" case, not a failure.
        let existing: SessionView | null = null;
        try {
          existing = await fetchSession(controller.signal);
        } catch (error) {
          if (!(error instanceof BenchApiError && error.isUnauthenticated)) {
            throw error;
          }
        }

        if (controller.signal.aborted) {
          return;
        }
        setBoot({
          kind: "ready",
          environments: list.environments,
          profiles: list.profiles,
          bench,
        });
        setSession(existing);
      } catch (error) {
        if (!controller.signal.aborted) {
          setBoot({ kind: "error", message: describe(error) });
        }
      }
    })();

    return () => controller.abort();
  }, []);

  const run = useCallback(async (action: () => Promise<SessionView | null>): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      setSession(await action());
    } catch (error) {
      // An expired session must drop the tester back to the connect panel, not leave a dead UI.
      if (error instanceof BenchApiError && error.isUnauthenticated) {
        setSession(null);
      }
      setActionError(describe(error));
    } finally {
      setBusy(false);
    }
  }, []);

  // Keep the token alive without asking the tester to think about it.
  //
  // The bench holds the token server-side, so expiry is not a security boundary here -- it is
  // just an interruption, and a "Renew" button only ever appears when it is already too late.
  // One timeout is scheduled per session view, cancelled and rescheduled whenever the server's
  // view changes. The callback is read through a ref so the effect depends on the deadline
  // alone: depending on the function would reschedule on every render, which is a request loop.
  const renewRef = useRef<() => Promise<void>>(async () => {});
  renewRef.current = async () => {
    try {
      setSession(await refreshSession());
      setRenewalFailed(false);
    } catch (error) {
      // Nothing to interrupt the tester with unless it stays broken: the session may simply have
      // been ended elsewhere, in which case an unauthenticated answer is the honest outcome.
      if (error instanceof BenchApiError && error.isUnauthenticated) {
        setSession(null);
        return;
      }
      setRenewalFailed(true);
    }
  };

  // Keyed on the session object, not on its seconds_remaining.
  //
  // Keying on the number looked equivalent and was not: a refresh that happens to report the same
  // remaining lifetime as the previous one leaves the dependency unchanged, so no new timer is
  // scheduled and renewal stops for good. Observed -- two renewals, then silence. Every session
  // view is a fresh object, so identity is the dependency that actually tracks "new deadline".
  useEffect(() => {
    if (session === null || session.header_only || session.seconds_remaining === null) {
      return;
    }
    const delay = Math.max(
      session.seconds_remaining - RENEW_MARGIN_SECONDS,
      MIN_RENEW_DELAY_SECONDS,
    );
    const timer = setTimeout(() => void renewRef.current(), delay * 1000);
    return () => clearTimeout(timer);
  }, [session]);

  const checkHealth = useCallback(async (environment: string): Promise<void> => {
    setHealthLoading(true);
    try {
      setAmsHealth(await fetchEnvironmentHealth(environment));
    } catch (error) {
      setAmsHealth({
        environment,
        health: {
          reachable: false,
          status: null,
          http_status: null,
          checks: null,
          llm: null,
          detail: describe(error),
        },
        gitea: null,
      });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  // Where the deployment mints its own tokens there is no sign-in to perform, so a session is
  // started on load rather than behind a screen asking which of one environment to use. Attempted
  // once per boot: a failure surfaces as actionError instead of retrying in a loop.
  const attempted = useRef(false);
  useEffect(() => {
    if (boot.kind !== "ready" || session !== null || attempted.current) {
      return;
    }
    const first = boot.environments[0];
    // The bench's own profile list rather than a name spelled out here: a deployment can be
    // configured with any set, and the first is the one the picker used to default to.
    const profile = boot.profiles[0];
    if (!first || !profile || first.auth_mode === "oidc") {
      return;
    }
    attempted.current = true;
    void (async () => {
      await run(() => startSession(first.name, profile));
      await checkHealth(first.name);
    })();
  }, [boot, session, run, checkHealth]);

  const connect = useCallback(
    async (environment: string, profile: string) => {
      await run(() => startSession(environment, profile));
      await checkHealth(environment);
    },
    [run, checkHealth],
  );

  const disconnect = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      await endSession();
      setSession(null);
      setAmsHealth(null);
    } catch (error) {
      setActionError(describe(error));
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    boot,
    session,
    amsHealth,
    healthLoading,
    busy,
    actionError,
    renewalFailed,
    connect,
    disconnect,
    selectProfile: (profile) => run(() => patchProfile(profile)),
    applyScope: (patch) => run(() => patchScope(patch)),
    checkHealth,
  };
}

function describe(error: unknown): string {
  return error instanceof BenchApiError ? error.message : `Unexpected failure: ${String(error)}`;
}
